const MAX_REPORT_CARD_FILE_BYTES = 10 * 1024 * 1024
const MAX_CLIENT_IMAGE_FILE_BYTES = 25 * 1024 * 1024
const MAX_IMAGE_DIMENSION = 2200
const TARGET_IMAGE_FILE_BYTES = 2.5 * 1024 * 1024
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif'])

export type PreparedReportCardFile = {
  displayFile: File
  uploadFile: File
  metadata: {
    kind: 'image' | 'pdf'
    mimeType: string
    originalSizeBytes: number
    uploadSizeBytes: number
    width: number | null
    height: number | null
    wasOptimized: boolean
  }
}

function isPdf(file: File) {
  return file.type === 'application/pdf'
}

function isImage(file: File) {
  return file.type.startsWith('image/') || isHeicImage(file)
}

function isHeicImage(file: File) {
  const fileType = file.type.toLowerCase()
  if (HEIC_MIME_TYPES.has(fileType)) {
    return true
  }

  return /\.hei[cf]$/i.test(file.name)
}

function renameFileWithExtension(fileName: string, extension: string) {
  const dotIndex = fileName.lastIndexOf('.')
  const baseName = dotIndex >= 0 ? fileName.slice(0, dotIndex) : fileName
  return `${baseName}.${extension}`
}

function normalizeHeicUploadFile(file: File) {
  if (HEIC_MIME_TYPES.has(file.type.toLowerCase())) {
    return file
  }

  return new File([file], file.name, {
    type: 'image/heic',
    lastModified: file.lastModified,
  })
}

function loadImageElement(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(image)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Kunne ikke lese bildet.'))
    }

    image.src = objectUrl
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Kunne ikke klargjøre bildet.'))
          return
        }

        resolve(blob)
      },
      type,
      quality
    )
  })
}

async function optimizeImageFile(file: File): Promise<PreparedReportCardFile> {
  const image = await loadImageElement(file)
  const originalWidth = image.naturalWidth
  const originalHeight = image.naturalHeight
  const largestDimension = Math.max(originalWidth, originalHeight)
  const shouldResize = largestDimension > MAX_IMAGE_DIMENSION
  const shouldCompress = file.size > TARGET_IMAGE_FILE_BYTES

  if (!shouldResize && !shouldCompress) {
    return {
      displayFile: file,
      uploadFile: file,
      metadata: {
        kind: 'image',
        mimeType: file.type,
        originalSizeBytes: file.size,
        uploadSizeBytes: file.size,
        width: originalWidth,
        height: originalHeight,
        wasOptimized: false,
      },
    }
  }

  const scale = shouldResize ? MAX_IMAGE_DIMENSION / largestDimension : 1
  const width = Math.max(1, Math.round(originalWidth * scale))
  const height = Math.max(1, Math.round(originalHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Kunne ikke klargjøre bildet.')
  }

  context.drawImage(image, 0, 0, width, height)

  let outputType = file.type
  let quality = outputType === 'image/png' ? undefined : 0.9
  let blob = await canvasToBlob(canvas, outputType, quality)

  if (file.type === 'image/png' && blob.size > TARGET_IMAGE_FILE_BYTES) {
    outputType = 'image/webp'
    quality = 0.9
    blob = await canvasToBlob(canvas, outputType, quality)
  }

  while (
    blob.size > TARGET_IMAGE_FILE_BYTES &&
    typeof quality === 'number' &&
    quality > 0.6
  ) {
    quality -= 0.08
    blob = await canvasToBlob(canvas, outputType, quality)
  }

  const uploadFile = new File(
    [blob],
    outputType === file.type
      ? file.name
      : renameFileWithExtension(
          file.name,
        outputType === 'image/webp' ? 'webp' : 'jpg'
        ),
    {
      type: outputType,
      lastModified: file.lastModified,
    }
  )

  return {
    displayFile: file,
    uploadFile,
    metadata: {
      kind: 'image',
      mimeType: outputType,
      originalSizeBytes: file.size,
      uploadSizeBytes: uploadFile.size,
      width,
      height,
      wasOptimized: uploadFile.size !== file.size || width !== originalWidth,
    },
  }
}

export function validateReportCardFile(file: File) {
  if (!isPdf(file) && !isImage(file)) {
    return 'Filtypen støttes ikke. Bruk PDF eller et bilde.'
  }

  if (isPdf(file) && file.size > MAX_REPORT_CARD_FILE_BYTES) {
    return 'PDF-en er for stor. Velg en fil under 10 MB.'
  }

  if (isHeicImage(file) && file.size > MAX_REPORT_CARD_FILE_BYTES) {
    return 'HEIC-bildet er for stort. Velg en fil under 10 MB.'
  }

  if (isImage(file) && file.size > MAX_CLIENT_IMAGE_FILE_BYTES) {
    return 'Bildet er for stort. Velg en fil under 25 MB.'
  }

  return null
}

export async function prepareReportCardFile(
  file: File
): Promise<PreparedReportCardFile> {
  const validationError = validateReportCardFile(file)
  if (validationError) {
    throw new Error(validationError)
  }

  if (isPdf(file)) {
    return {
      displayFile: file,
      uploadFile: file,
      metadata: {
        kind: 'pdf',
        mimeType: file.type,
        originalSizeBytes: file.size,
        uploadSizeBytes: file.size,
        width: null,
        height: null,
        wasOptimized: false,
      },
    }
  }

  if (isHeicImage(file)) {
    const uploadFile = normalizeHeicUploadFile(file)

    return {
      displayFile: file,
      uploadFile,
      metadata: {
        kind: 'image',
        mimeType: uploadFile.type,
        originalSizeBytes: file.size,
        uploadSizeBytes: uploadFile.size,
        width: null,
        height: null,
        wasOptimized: false,
      },
    }
  }

  return optimizeImageFile(file)
}
